import React, { useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PrivacyField as PrivacyFieldType } from '../../types/interfaces';

interface PrivacyFieldProps {
  label: string;
  field: PrivacyFieldType;
  onUpdate: (updatedField: PrivacyFieldType) => void;
  readonly?: boolean;
}

const getPrivacyIcon = (privacy: string) => {
  switch (privacy) {
    case 'everyone':
      return <PublicIcon fontSize="small" color="success" />;
    case 'friends':
      return <PersonIcon fontSize="small" color="primary" />;
    case 'none':
      return <LockIcon fontSize="small" color="error" />;
    default:
      return <LockIcon fontSize="small" />;
  }
};

const getPrivacyColor = (privacy: string) => {
  switch (privacy) {
    case 'everyone':
      return 'success';
    case 'friends':
      return 'primary';
    case 'none':
      return 'error';
    default:
      return 'default';
  }
};

const PrivacyFieldDisplay: React.FC<PrivacyFieldProps> = ({ 
  label, 
  field, 
  onUpdate, 
  readonly = false 
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editedField, setEditedField] = useState<PrivacyFieldType>(field);
  const [newUserId, setNewUserId] = useState('');

  const handleSave = () => {
    onUpdate(editedField);
    setModalOpen(false);
  };

  const handleCancel = () => {
    setEditedField(field);
    setModalOpen(false);
  };

  const addUser = () => {
    if (newUserId.trim() && !editedField.allowedUsers.includes(newUserId.trim())) {
      setEditedField({
        ...editedField,
        allowedUsers: [...editedField.allowedUsers, newUserId.trim()]
      });
      setNewUserId('');
    }
  };

  const removeUser = (userId: string) => {
    setEditedField({
      ...editedField,
      allowedUsers: editedField.allowedUsers.filter(id => id !== userId)
    });
  };

  const blockedUsers = field.allowedUsers.filter(id => id.endsWith('.blocked'));
  const allowedUsers = field.allowedUsers.filter(id => !id.endsWith('.blocked'));

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {label}:
        </Typography>
        <Chip
          icon={getPrivacyIcon(field.globalPrivacy)}
          label={field.globalPrivacy}
          size="small"
          color={getPrivacyColor(field.globalPrivacy) as any}
          variant="outlined"
        />
        {!readonly && (
          <IconButton size="small" onClick={() => setModalOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Show allowed/blocked users summary */}
      <Box sx={{ ml: 2 }}>
        {allowedUsers.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="success.main">
              Explicitly allowed: {allowedUsers.length} user(s)
            </Typography>
          </Box>
        )}
        {blockedUsers.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="error.main">
              Explicitly blocked: {blockedUsers.length} user(s)
            </Typography>
          </Box>
        )}
      </Box>

      {/* Privacy Edit Modal */}
      <Dialog open={modalOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>Edit Privacy for {label}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Global Privacy Setting
            </Typography>
            <FormControl fullWidth>
              <Select
                value={editedField.globalPrivacy}
                onChange={(e) => setEditedField({
                  ...editedField,
                  globalPrivacy: e.target.value as 'everyone' | 'friends' | 'none'
                })}
              >
                <MenuItem value="everyone">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon fontSize="small" color="success" />
                    Everyone
                  </Box>
                </MenuItem>
                <MenuItem value="friends">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="primary" />
                    Friends Only
                  </Box>
                </MenuItem>
                <MenuItem value="none">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon fontSize="small" color="error" />
                    No One
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Individual User Overrides
            </Typography>
            <Typography variant="caption" color="text.secondary" paragraph>
              Add specific users to allow or block. Use ".blocked" suffix to block (e.g., "userId.blocked").
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="User ID or User ID.blocked"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addUser()}
                fullWidth
              />
              <Button
                variant="contained"
                size="small"
                onClick={addUser}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>

            {editedField.allowedUsers.length > 0 && (
              <List dense>
                {editedField.allowedUsers.map((userId) => (
                  <ListItem key={userId}>
                    <ListItemText
                      primary={userId}
                      secondary={userId.endsWith('.blocked') ? 'Blocked user' : 'Allowed user'}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => removeUser(userId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivacyFieldDisplay;
